import { useState, useEffect } from "react";
import { Button, Input, Drawer, Form, Typography, Space, Card } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { CloseOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import {
  createAttributesApi,
  getAttributesApi,
  updateAttributesApi,
  deleteAttributesApi,
} from "../../../../redux/action/Attributes";
import { resetDeleteAttributes } from "../../../../redux/slice/Attributes";
import DeleteConfirmationModal from "../DeleteConfirmationModal";

const { TextArea } = Input;
const { Title, Text } = Typography;

export default function OrganizationConfiguration() {
  const dispatch = useDispatch();
  const { createAttribute, createAttributeLoading, createAttributeError } =
    useSelector((state) => state.CREATE_ATTRIBUTES);
  const { updateAttribute, updateAttributeLoading, updateAttributeError } =
    useSelector((state) => state.UPDATE_ATTRIBUTES);
  const { deleteAttribute, deleteAttributeLoading, deleteAttributeError } =
    useSelector((state) => state.DELETE_ATTRIBUTES);
  const { attributes, attributesSuccess, attributesError } = useSelector(
    (state) => state.GET_ATTRIBUTES
  );
  const [departments, setDepartments] = useState([]);

  const [locations, setLocations] = useState([]);

  const [groups, setGroups] = useState([]);

  const [drawerState, setDrawerState] = useState({
    isOpen: false,
    type: null,
    mode: "add",
  });

  const [deleteModalState, setDeleteModalState] = useState({
    isOpen: false,
    item: null,
    type: null,
  });

  const [form] = Form.useForm();
  const [newItemName, setNewItemName] = useState("");

  const openDrawer = (type, mode, item) => {
    setDrawerState({ isOpen: true, type, mode, item });
    if (mode === "edit" && item) {
      form.setFieldsValue({
        name: item.name,
        description: item.description || "",
      });
    } else {
      form.resetFields();
    }
  };

  const closeDrawer = () => {
    setDrawerState({ isOpen: false, type: null, mode: "add" });
    form.resetFields();
  };

  const handleSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (!values.name?.trim() || !drawerState.type) return;

        let apiType;

        if (drawerState.mode === "add") {
          if (drawerState.type === "department") {
            apiType = "DEPARTMENT";
          } else if (drawerState.type === "location") {
            apiType = "Location";
          } else {
            apiType = "GROUP";
          }
          let payload = JSON.stringify([
            {
              organizationAttributesId: null,
              attributeType: apiType,
              attributeValue: values.name,
            },
          ]);
          dispatch(createAttributesApi(payload));
        } else if (drawerState.mode === "edit" && drawerState.item) {
          if (drawerState.type === "department") {
            apiType = "DEPARTMENT";
          } else if (drawerState.type === "location") {
            apiType = "Location";
          } else {
            apiType = "GROUP";
          }
          let payload = JSON.stringify([
            {
              organizationAttributesId: drawerState.item.id,
              attributeType: apiType,
              attributeValue: values.name,
            },
          ]);
          dispatch(updateAttributesApi(payload));
        }

        closeDrawer();
      })
      .catch((error) => {
        console.log("Validation failed:", error);
      });
  };

  const addNewItem = (type) => {
    if (!newItemName.trim()) return;

    const newItem = {
      id: Date.now().toString(),
      name: newItemName,
      description: "",
    };

    switch (type) {
      case "department":
        setDepartments((prev) => [...prev, newItem]);
        break;
      case "location":
        setLocations((prev) => [...prev, newItem]);
        break;
      case "group":
        setGroups((prev) => [...prev, newItem]);
        break;
    }

    setNewItemName("");
  };

  const openDeleteModal = (type, item) => {
    setDeleteModalState({ isOpen: true, item, type });
  };

  const closeDeleteModal = () => {
    setDeleteModalState({ isOpen: false, item: null, type: null });
  };

  const handleDelete = () => {
    const { item, type } = deleteModalState;
    if (!item || !item.id) return;

    // Call the API to delete
    dispatch(deleteAttributesApi(item.id));

    // Close modal immediately - success/error will be handled in useEffect
    closeDeleteModal();
  };

  const getTitle = () => {
    if (!drawerState.type) return "";
    const typeMap = {
      department: "Department",
      location: "Location",
      group: "Group",
    };
    return `${drawerState.mode === "add" ? "Add" : "Edit"} ${
      typeMap[drawerState.type]
    }`;
  };

  const renderColumn = (title, items, type) => (
    <div className="flex-1 min-w-0">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 mt-2 gap-2">
        <Title level={5} className="!text-[15px] font-poppins !text-[#171717] !font-semibold !mb-0">
          {title}
        </Title>
        <Button
          type="link"
          className="font-poppins self-start sm:self-auto"
          onClick={() => openDrawer(type, "add")}
          style={{ color: "#2563EB", fontWeight: 600, fontSize: "13px" }}
        >
          Add New
        </Button>
      </div>
      <Card
        className="min-h-[300px] sm:min-h-[400px] shadow-sm"
        style={{
          backgroundColor: "#F8FAFC",
          border: "1px solid #E2E8F0",
          borderRadius: "16px",
        }}
        bodyStyle={{ padding: "8px sm:10px" }}
      >
        <Space direction="vertical" className="w-full" size="small">
          {items.map((item) => (
            <div
              key={item.id}
              className="flex items-center justify-between bg-white rounded-xl border-[1px] border-solid border-[#E2E8F0] px-3 py-2 hover:border-[#CBD5E1] transition-colors"
            >
              <Text className="text-[13px] !text-[#334155] font-poppins font-medium">
                {item.name}
              </Text>
              <div className="flex items-center gap-2">
                <Button
                  type="link"
                  size="small"
                  onClick={() => openDrawer(type, "edit", item)}
                  style={{ color: "#2563EB", fontWeight: 600 }}
                  className="font-poppins"
                >
                  Edit
                </Button>
                <Button
                  type="link"
                  size="small"
                  icon={<CloseCircleOutlined />}
                  onClick={() => openDeleteModal(type, item)}
                  style={{ color: "#DC2626", fontWeight: 500, fontSize: "18px" }}
                  className="font-poppins"
                />
              </div>
            </div>
          ))}
          {/* <div className="flex items-center justify-between bg-white/20 rounded px-3 py-2">
            <Input
              placeholder="Type here..."
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              onPressEnter={() => addNewItem(type)}
              style={{
                border: "none",
                backgroundColor: "transparent",
                color: "#374151",
                boxShadow: "none",
              }}
            />
            <Button
              type="link"
              size="small"
              onClick={() => addNewItem(type)}
              style={{ color: "#0B4F9F", fontWeight: 500 }}
            >
              Edit
            </Button>
          </div> */}
        </Space>
      </Card>
    </div>
  );

  const getAttributes = () => {
    dispatch(getAttributesApi());
  };

  const filterAttributes = () => {
    const extractItems = (key) => {
      return (
        attributes[key]?.map((item) => ({
          id: item.organizationAttributesId,
          name: item.attributeValue,
        })) || []
      );
    };

    setDepartments(extractItems("DEPARTMENT"));
    setLocations(extractItems("LOCATION"));
    setGroups(extractItems("GROUP"));

    console.log(extractItems("DEPARTMENT"));
  };

  useEffect(() => {
    getAttributes();
  }, []);

  useEffect(() => {
    if (attributes) {
      filterAttributes();
    }
  }, [attributes, attributesSuccess, attributesError]);

  useEffect(() => {
    if (createAttribute && !createAttributeLoading) {
      toast.success("Created successfully!");
      getAttributes();
    }
  }, [createAttribute, createAttributeLoading]);

  useEffect(() => {
    if (createAttributeError) {
      toast.error(createAttributeError || "Failed to create. Please try again.");
    }
  }, [createAttributeError]);

  useEffect(() => {
    if (updateAttribute && !updateAttributeLoading) {
      toast.success("Updated successfully!");
      getAttributes();
    }
  }, [updateAttribute, updateAttributeLoading]);

  useEffect(() => {
    if (updateAttributeError) {
      toast.error(updateAttributeError || "Failed to update. Please try again.");
    }
  }, [updateAttributeError]);

  useEffect(() => {
    if (deleteAttribute && !deleteAttributeLoading) {
      toast.success("Deleted successfully!");
      getAttributes();
      // Reset state after showing success toast
      dispatch(resetDeleteAttributes());
    }
  }, [deleteAttribute, deleteAttributeLoading]);

  useEffect(() => {
    if (deleteAttributeError) {
      toast.error(deleteAttributeError || "Failed to delete. Please try again.");
      // Reset state after showing error toast
      dispatch(resetDeleteAttributes());
    }
  }, [deleteAttributeError]);

  return (
    <div className="font-poppins pt-1 min-h-screen">
      <span
        level={2}
        className="!mb-6 sm:!mb-8 md:!mb-12 text-base font-poppins text-[#171717] font-semibold"
      >
        Organization Configuration
      </span>

      <div className="flex flex-col lg:flex-row gap-4 md:gap-6">
        {renderColumn("Department", departments, "department")}
        {renderColumn("Location", locations, "location")}
        {renderColumn("Group", groups, "group")}
      </div>

      <Drawer
        title={
          <div className="flex items-center justify-between">
            <Title level={4} className="!mb-0 !text-base !font-semibold !text-[#171717] font-poppins">
              {getTitle()}
            </Title>
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={closeDrawer}
              size="small"
            />
          </div>
        }
        placement="right"
        width={400}
        open={drawerState.isOpen}
        onClose={closeDrawer}
        closable={false}
        className="font-poppins"
        footer={
          <div className="flex gap-3">
            <Button
              onClick={closeDrawer}
              className="flex-1 h-10 rounded-xl text-[13px] font-semibold font-poppins bg-white hover:!bg-[#F8FAFC]"
              style={{ borderColor: "#E2E8F0", color: "#334155" }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleSubmit}
              className="flex-1 btn-gradient h-10 rounded-xl border-0 text-[13px] font-semibold font-poppins"
            >
              {drawerState.mode === "add" ? "Add" : "Update"}
            </Button>
          </div>
        }
      >
        <Form form={form} layout="vertical" className="space-y-6">
          <Form.Item
            label={
              <span>
                {drawerState.type === "department"
                  ? "Department"
                  : drawerState.type === "location"
                  ? "Location"
                  : "Group"}{" "}
                Name
                <span style={{ color: "#ef4444", marginLeft: "4px" }}>*</span>
              </span>
            }
            name="name"
            rules={[
              { required: true, message: "Please input the name!" },
              { whitespace: true, message: "Name cannot be empty!" },
            ]}
          >
            <Input
              placeholder="Type here"
              className="h-10 rounded-xl shadow-sm text-sm font-poppins"
              style={{ backgroundColor: "#FFFFFF", borderColor: "#E2E8F0", color: "#171717" }}
            />
          </Form.Item>

          {/* <Form.Item
            label={
              <div className="flex items-center justify-between w-full">
                <span>Description</span>
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  Max 300 character
                </Text>
              </div>
            }
            name="description"
          >
            <TextArea
              placeholder="Type here"
              rows={5}
              maxLength={300}
              showCount
              style={{
                backgroundColor: "#F5F7FA",
                borderColor: "#e5e7eb",
                resize: "none",
              }}
            />
          </Form.Item> */}
        </Form>
      </Drawer>

      <DeleteConfirmationModal
        isOpen={deleteModalState.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        itemType={deleteModalState.type || "item"}
        itemName={deleteModalState.item?.name}
      />
    </div>
  );
}
